
import React, { useRef, useState } from 'react';
import { motion, PanInfo, useAnimation } from 'framer-motion';
import { Trash2 } from 'lucide-react';

interface SwipeableCartItemProps {
    children: React.ReactNode;
    onRemove: () => void;
}

const SwipeableCartItem: React.FC<SwipeableCartItemProps> = ({ children, onRemove }) => {
    const controls = useAnimation();
    const [isRemoving, setIsRemoving] = useState(false);

    const handleDragEnd = async (_: any, info: PanInfo) => {
        const offset = info.offset.x;
        const velocity = info.velocity.x;

        // Threshold to trigger remove (swipe left)
        if (offset < -100 || (offset < -50 && velocity < -500)) {
            setIsRemoving(true);
            // Animate off screen
            await controls.start({ x: -500, opacity: 0 });
            onRemove();
        } else {
            // Reset position
            controls.start({ x: 0, opacity: 1 });
        }
    };

    if (isRemoving) {
        return null; // Or keep it hidden until parent removes it
    }

    return (
        <div className="relative overflow-hidden rounded-[2rem] border border-gray-100 dark:border-gray-800">
            {/* Background Action Layer (Rose/Red for Remove) */}
            <div className="absolute inset-0 bg-rose-500 flex items-center justify-end px-8 rounded-[2rem]">
                <Trash2 className="text-white" size={28} strokeWidth={3} />
            </div>

            {/* Foreground Content */}
            <motion.div
                drag="x"
                dragConstraints={{ left: -1000, right: 0 }} // Only swipe left
                dragElastic={0.1}
                onDragEnd={handleDragEnd}
                animate={controls}
                className="relative bg-white dark:bg-gray-950 z-10 p-4"
                whileTap={{ cursor: "grabbing" }}
            >
                {children}
            </motion.div>
        </div>
    );
};

export default SwipeableCartItem;
